import { useState, useRef, useEffect } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import countryCodes from "../data/countryCodes";

/**
 * Searchable country code picker.
 * Shows flag + code, opens a dropdown with search to find any country.
 */
export default function CountryCodePicker({ value = "91", onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Find selected country — match by code + country name for duplicates (US/Canada both +1)
  const selected = countryCodes.find((c) => c.code === value) || countryCodes.find((c) => c.code === "91");

  const filtered = (() => {
    const term = search.trim().toLowerCase();
    if (!term) return countryCodes;

    // Score-based: starts-with gets priority
    const results = countryCodes
      .map((c) => {
        const countryLower = c.country.toLowerCase();
        const codeFull = `+${c.code}`;
        let score = 0;
        if (countryLower.startsWith(term)) score = 3;
        else if (countryLower.includes(term)) score = 2;
        else if (c.code.startsWith(term) || codeFull.startsWith(term)) score = 2;
        else if (c.code.includes(term)) score = 1;
        // Also match common abbreviations
        const abbr = c.country.split(" ").map((w) => w[0]).join("").toLowerCase();
        if (abbr === term || abbr.startsWith(term)) score = Math.max(score, 3);
        return { ...c, score };
      })
      .filter((c) => c.score > 0)
      .sort((a, b) => b.score - a.score || a.country.localeCompare(b.country));

    return results;
  })();

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(""); }}
        className="flex items-center gap-1 border-r border-sand-300 pr-2.5 text-[14px] font-bold text-brand-700 outline-none"
      >
        <span className="text-[16px]">{selected.flag}</span>
        <span>+{selected.code}</span>
        <ChevronDown size={12} className="text-ink-400" />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-72 overflow-hidden rounded-2xl border border-sand-200 bg-white shadow-elevated sm:w-80">
          {/* Search */}
          <div className="flex items-center gap-2 border-b border-sand-200 px-3 py-2.5">
            <Search size={15} className="shrink-0 text-ink-400" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country or code..."
              className="w-full bg-transparent text-[13px] text-ink-800 placeholder:text-ink-300 outline-none"
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} className="shrink-0 text-ink-400 hover:text-brand-600">
                <X size={13} />
              </button>
            )}
          </div>

          {/* List */}
          <ul className="max-h-60 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-center text-[12.5px] text-ink-400">No country found</li>
            ) : (
              filtered.map((c, idx) => {
                const isSelected = c.code === value && c.country === selected.country;
                return (
                  <li key={`${c.code}-${c.country}-${idx}`}>
                    <button
                      type="button"
                      onClick={() => {
                        onChange(c.code);
                        setOpen(false);
                        setSearch("");
                      }}
                      className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition ${
                        isSelected
                          ? "bg-brand-50 text-brand-700"
                          : "text-ink-700 hover:bg-sand-50"
                      }`}
                    >
                      <span className="text-[18px]">{c.flag}</span>
                      <span className="flex-1 truncate text-[13px] font-medium">{c.country}</span>
                      <span className="text-[12.5px] font-semibold text-ink-400">+{c.code}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
